import React, { forwardRef } from "react";
import { Button, CircularProgress, Tooltip } from "@mui/material";
import { ds } from "../../styles/designSystem";

import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

/**
 * 시스템 전역 액션 버튼 공통 컴포넌트.
 *
 * <p>2D 키비주얼 모달의 디자인을 표준으로 채택해 시스템 전체 버튼을 일관되게 만든다.
 * variant 가 색·아이콘·라벨·로딩 문구·hover 상태까지 책임지고, 사용처에서는 onClick·loading 만 신경 쓰면 된다.
 *
 * @example
 *   <ActionButton variant="edit"     onClick={handleEdit} />
 *   <ActionButton variant="delete"   onClick={handleDelete}   loading={deleting} />
 *   <ActionButton variant="download" onClick={handleDownload} loading={downloading} />
 *   <ActionButton variant="save"     type="submit"            loading={saving} />
 *   <ActionButton variant="close"    onClick={onClose} />
 *   <ActionButton variant="primary"  onClick={...}>커스텀 라벨</ActionButton>
 *
 * variant 별 디자인 표준:
 *   - primary/save/close/confirm/download/upload/add → contained, primary 색
 *   - secondary/edit/cancel/refresh                  → outlined, 검정 글씨 + 회색 border
 *   - delete                                         → outlined, 빨강 글씨 + 빨강 border
 *
 * @param {string}    variant    download | edit | delete | close | save | cancel | confirm | add | refresh | upload | search | primary | secondary
 * @param {boolean}   loading    진행 중 — 스피너 + loadingLabel 자동
 * @param {string}    label      variant 기본 라벨을 override
 * @param {ReactNode} startIcon  variant 기본 아이콘 override (false 로 두면 아이콘 숨김)
 * @param {string}    tooltip    있으면 Tooltip 으로 감쌈
 * @param {object}    sx         추가 sx 병합
 * @param {...any}    rest       나머지 MUI Button props (onClick, disabled, type, size, fullWidth, color 등)
 */
const PILL_BASE = {
    textTransform: "none",
    borderRadius: 1.1,
    fontWeight: 700,
};

const SX = {
    primaryFill: {
        ...PILL_BASE,
        bgcolor: ds.colors.primary,
        color: "#fff",
        "&:hover": { bgcolor: ds.colors.primaryDark },
    },
    neutralOutline: {
        ...PILL_BASE,
        borderColor: "#cbd5e1",
        color: "#0f172a",
        "&:hover": { borderColor: ds.colors.primary, bgcolor: ds.colors.primaryBg },
    },
    dangerOutline: {
        ...PILL_BASE,
        color: "#dc2626",
        borderColor: "rgba(220,38,38,0.35)",
        "&:hover": { borderColor: "#dc2626", bgcolor: "rgba(220,38,38,0.06)" },
    },
};

const VARIANTS = {
    // contained primary 계열
    primary:   { mui: "contained", icon: null,                                    label: null,        loadingLabel: "처리 중...",     sx: SX.primaryFill },
    save:      { mui: "contained", icon: null,     label: "저장",      loadingLabel: "저장 중...",     sx: SX.primaryFill },
    close:     { mui: "contained", icon: null,                                    label: "닫기",      loadingLabel: "처리 중...",     sx: SX.primaryFill },
    confirm:   { mui: "contained", icon: <CheckRoundedIcon sx={{ fontSize: 16 }} />,    label: "확인",      loadingLabel: "처리 중...",     sx: SX.primaryFill },
    download:  { mui: "contained", icon: <DownloadRoundedIcon sx={{ fontSize: 14 }} />, label: "다운로드",  loadingLabel: "다운로드 중...", sx: SX.primaryFill },
    upload:    { mui: "contained", icon: <UploadRoundedIcon sx={{ fontSize: 14 }} />,   label: "업로드",    loadingLabel: "업로드 중...",   sx: SX.primaryFill },
    add:       { mui: "contained", icon: <AddRoundedIcon sx={{ fontSize: 16 }} />,      label: "추가",      loadingLabel: "처리 중...",     sx: { ...SX.primaryFill, minWidth: 72 } },
    search:    { mui: "contained", icon: <SearchRoundedIcon sx={{ fontSize: 16 }} />,   label: "검색",      loadingLabel: "검색 중...",     sx: SX.primaryFill },

    // outlined 계열
    secondary: { mui: "outlined",  icon: null,                                    label: null,        loadingLabel: "처리 중...",     sx: SX.neutralOutline },
    edit:      { mui: "outlined",  icon: null,            label: "수정",      loadingLabel: "처리 중...",     sx: SX.neutralOutline },
    cancel:    { mui: "outlined",  icon: null,                                    label: "취소",      loadingLabel: "처리 중...",     sx: SX.neutralOutline },
    refresh:   { mui: "outlined",  icon: <RefreshRoundedIcon sx={{ fontSize: 16 }} />,  label: "새로고침",  loadingLabel: "새로고침 중...", sx: SX.neutralOutline },

    // 빨강 outlined
    delete:    { mui: "outlined",  icon: null,   label: "삭제",      loadingLabel: "삭제 중...",     sx: SX.dangerOutline },
};

const ActionButton = forwardRef(function ActionButton({
    variant = "primary",
    loading = false,
    label,
    startIcon,
    tooltip,
    size = "small",
    sx,
    disabled,
    children,
    ...rest
}, ref) {
    const cfg = VARIANTS[variant] || VARIANTS.primary;

    const finalLabel = loading
        ? cfg.loadingLabel
        : (label ?? children ?? cfg.label);

    const finalIcon = loading
        ? <CircularProgress size={14} sx={{ color: "inherit" }} />
        : (startIcon === false ? null : (startIcon ?? cfg.icon));

    const mergedSx = sx
        ? (Array.isArray(sx) ? [cfg.sx, ...sx] : { ...cfg.sx, ...sx })
        : cfg.sx;

    const button = (
        <Button
            ref={ref}
            variant={cfg.mui}
            size={size}
            startIcon={finalIcon}
            disabled={disabled || loading}
            sx={mergedSx}
            {...rest}
        >
            {finalLabel}
        </Button>
    );

    if (tooltip) {
        return (
            <Tooltip title={tooltip}>
                <span>{button}</span>
            </Tooltip>
        );
    }
    return button;
});

export default ActionButton;
